import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { broadcastOnce, isNotDefined } from '@enterprise/core';

import type { MailingDraft } from './types/mailing-draft.type';
import type { NthCalculatorStore } from './types/nth-calculator-store.type';

const rawTtl = Number.parseFloat(import.meta.env.VITE_NTH_DRAFT_TTL_HOURS ?? '');
const ttlHours = Number.isFinite(rawTtl) ? rawTtl : 2;

const NTH_CALCULATOR_CHANNEL = 'datastream:nth-calculator';

export const useNthCalculatorStore = create<NthCalculatorStore>()(
  persist(
    (set) => {
      return {
        clearDraft: (mailingId) => {
          set((state) => {
            const { [mailingId]: _, ...rest } = state.drafts;

            return { drafts: rest as Record<number, MailingDraft> };
          });
          broadcastOnce(NTH_CALCULATOR_CHANNEL, { mailingId, type: 'draft-updated' });
        },
        drafts: {},
        isPopout: false,
        setDraft: (mailingId, rows) => {
          set((state) => {
            return {
              drafts: {
                ...state.drafts,
                [mailingId]: { rows, updatedAt: Date.now() },
              },
            };
          });
          broadcastOnce(NTH_CALCULATOR_CHANNEL, { mailingId, type: 'draft-updated' });
        },
        setIsPopout: (value) => {
          set({ isPopout: value });
        },
      };
    },
    {
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<NthCalculatorStore>;

        if (isNotDefined(persisted.drafts)) {
          return currentState;
        }

        const cutoff = Date.now() - ttlHours * 3_600_000;
        const filtered: Record<number, MailingDraft> = {};

        for (const [key, draft] of Object.entries(persisted.drafts)) {
          if (
            isNotDefined(draft) ||
            typeof draft !== 'object' ||
            !Number.isFinite(draft.updatedAt) ||
            !Array.isArray(draft.rows)
          ) {
            continue;
          }

          if (draft.updatedAt >= cutoff) {
            filtered[Number(key)] = draft;
          }
        }

        return { ...currentState, drafts: filtered };
      },
      name: 'datastream:nth-calculator:drafts',
      partialize: (state) => {
        return { drafts: state.drafts };
      },
      storage: createJSONStorage(() => {
        return localStorage;
      }),
      version: 1,
    }
  )
);
